import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  type CellContext,
  type ColumnDef,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  useReportErrors,
  useReportItems,
  useReportVersionHeader,
} from "@/hooks/useReportQueries";
import { handleApiError } from "@/hooks/useApiQueries";
import {
  Accordion,
  Button,
  ErrorDisplay,
  FormInput,
  HeadlessTable,
  Loader,
  NoData,
  PageTitle,
  StatusIndicator,
} from "@/shared/components";
import { useBreadcrumb } from "@/shared/hooks/useBreadcrumb";
import { useEffect, useMemo, useState } from "react";
import { formatReportingPeriod } from "@/utils/dateUtils";
import type { UiValidationError } from "@/generated/ui-api";
import { useAuth } from "@/auth";
import { useVersionColumns } from "@/hooks/useVersionColumns";

export default function ErrorsPage() {
  const { t } = useTranslation();
  const { versionId: versionIdString } = useParams<{ versionId: string }>();
  const versionId = versionIdString ? Number(versionIdString) : 0;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { representedEntity } = useAuth();

  const page = Number(searchParams.get("page")) || 1;
  const pageSize = 10;

  const [codeFilter, setCodeFilter] = useState(searchParams.get("code") || "");
  const [titleFilter, setTitleFilter] = useState(
    searchParams.get("title") || ""
  );
  const [reportItemNumberFrom, setReportItemNumberFrom] = useState(
    searchParams.get("reportItemNumberFrom") || ""
  );
  const [reportItemNumberTo, setReportItemNumberTo] = useState(
    searchParams.get("reportItemNumberTo") || ""
  );

  const [impactError, setImpactError] = useState(
    searchParams.get("impactError") !== "false"
  );
  const [impactWarning, setImpactWarning] = useState(
    searchParams.get("impactWarning") !== "false"
  );
  const [scopeReport, setScopeReport] = useState(
    searchParams.get("scopeReport") !== "false"
  );
  const [scopeItem, setScopeItem] = useState(
    searchParams.get("scopeItem") !== "false"
  );

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  useEffect(() => {
    setCodeFilter(searchParams.get("code") || "");
    setTitleFilter(searchParams.get("title") || "");
    setReportItemNumberFrom(searchParams.get("reportItemNumberFrom") || "");
    setReportItemNumberTo(searchParams.get("reportItemNumberTo") || "");
    setImpactError(searchParams.get("impactError") !== "false");
    setImpactWarning(searchParams.get("impactWarning") !== "false");
    setScopeReport(searchParams.get("scopeReport") !== "false");
    setScopeItem(searchParams.get("scopeItem") !== "false");
  }, [searchParams]);

  const {
    data: headerData,
    isLoading: loadingHeader,
    error: headerError,
    isError: isHeaderError,
  } = useReportVersionHeader(versionId, !!representedEntity);

  const isEditable = headerData?.version?.isEditable ?? false;

  const {
    data: errorsResponse,
    isLoading: loadingErrors,
    error: errorsError,
    isError: isErrorsError,
  } = useReportErrors(versionId, page, pageSize, {
    code: searchParams.get("code") || undefined,
    title: searchParams.get("title") || undefined,
    reportItemNumberFrom: searchParams.get("reportItemNumberFrom") || undefined,
    reportItemNumberTo: searchParams.get("reportItemNumberTo") || undefined,
    impactError: searchParams.get("impactError") !== "false",
    impactWarning: searchParams.get("impactWarning") !== "false",
    scopeReport: searchParams.get("scopeReport") !== "false",
    scopeItem: searchParams.get("scopeItem") !== "false",
    enabled: !!representedEntity,
  });

  const errorsData = errorsResponse?.errors;
  const pagination = errorsResponse?.pagination;

  const { data: itemsData } = useReportItems(versionId, 1, 1, {
    enabled: !!representedEntity,
  });

  const tradeDirection = itemsData?.reportHeader.tradeDirection;

  const { versionColumns, getTradeDirectionLabel } =
    useVersionColumns(tradeDirection);

  const { setBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    if (itemsData) {
      const breadcrumbs: { label: string; path?: string }[] = [
        { label: t("breadcrumbs.reports"), path: "/reports/summaries" },
        {
          label: `${getTradeDirectionLabel(
            itemsData.reportHeader.tradeDirection
          )} ${formatReportingPeriod(
            itemsData.reportHeader.reportingPeriod.reportingYear,
            itemsData.reportHeader.reportingPeriod.reportingMonth
          )}`,
          path: `/report/${itemsData.reportHeader.reportId}`,
        },
        {
          label: `${t("breadcrumbs.itemList")} ${
            itemsData.version?.versionNumber ?? ""
          }`,
          path: `/reports/versions/${versionId}/items`,
        },
        {
          label: `${t("breadcrumbs.errors")}`,
        },
      ];

      setBreadcrumbs(breadcrumbs);
    }
  }, [itemsData, t, setBreadcrumbs, getTradeDirectionLabel, versionId]);

  const errorColumns = useMemo<ColumnDef<UiValidationError>[]>(
    () => [
      {
        id: "actions",
        header: "",
        size: 120,
        cell: ({ row }) => {
          const error = row.original;
          const handleNavigate = () => {
            if (error.reportItemId) {
              navigate(
                `/reports/versions/${versionId}/items/${error.reportItemId}`
              );
            } else if (
              error.validationRuleScope === "REPORT" ||
              error.validationRuleScope === "HEADER"
            ) {
              navigate(`/reports/versions/${versionId}/items`);
            }
          };

          return (
            <div className="flex justify-center items-center px-2">
              <Button
                variant={!isEditable ? "secondary" : "primary"}
                size="md"
                onClick={handleNavigate}
              >
                {!isEditable
                  ? t("reportItemsPage.buttons.open")
                  : t("reportItemsPage.buttons.edit")}
              </Button>
            </div>
          );
        },
      },
      {
        accessorKey: "reportItemNumber",
        id: "reportItemNumber",
        header: t("errorsPage.table.reportItemNumber"),
        size: 60,
        cell: (info: CellContext<UiValidationError, unknown>) => (
          <div className="flex justify-center items-center">
            {(info.getValue() as string | number) ?? ""}
          </div>
        ),
      },
      {
        accessorKey: "code",
        id: "code",
        header: t("errorsPage.table.code"),
        size: 100,
        cell: (info: CellContext<UiValidationError, unknown>) => (
          <div className="flex justify-center items-center">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        accessorKey: "messageText",
        id: "messageText",
        header: t("errorsPage.table.title"),
        size: 500,
        cell: (info: CellContext<UiValidationError, unknown>) => (
          <div className="text-left px-4 py-2 wrap-break-word">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        accessorKey: "validationRuleScope",
        id: "validationRuleScope",
        header: t("errorsPage.table.validationRuleScope"),
        size: 120,
        cell: (info: CellContext<UiValidationError, unknown>) => {
          const scope = info.getValue() as string;
          return (
            <div className="flex justify-center items-center">
              {scope
                ? (t(`validationRuleScope.${scope}`, {
                    defaultValue: scope,
                  }) as string)
                : ""}
            </div>
          );
        },
      },
      {
        accessorKey: "validationRuleImpact",
        id: "validationRuleImpact",
        header: t("errorsPage.table.validationRuleImpact"),
        size: 150,
        cell: (info: CellContext<UiValidationError, unknown>) => {
          const impact = info.getValue() as string;
          return (
            <div className="flex justify-center items-center space-x-2">
              <StatusIndicator
                status={impact === "ERROR" ? "error" : "warning"}
                size="md"
              />
              <span>
                {impact
                  ? (t(`validationRuleImpact.${impact}`, {
                      defaultValue: impact,
                    }) as string)
                  : ""}
              </span>
            </div>
          );
        },
      },
    ],
    [t, isEditable, navigate, versionId]
  );

  const versionData = useMemo(
    () =>
      headerData?.version && itemsData
        ? [
            {
              ...headerData.version,
              reportingPeriod: formatReportingPeriod(
                itemsData.reportHeader.reportingPeriod.reportingYear,
                itemsData.reportHeader.reportingPeriod.reportingMonth
              ),
              tradeDirection: itemsData.reportHeader.tradeDirection,
            },
          ]
        : [],
    [headerData, itemsData]
  );

  const versionTable = useReactTable({
    data: versionData,
    columns: versionColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const errorsTable = useReactTable({
    data: errorsData || [],
    columns: errorColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handlePageChange = (newPage: number) => {
    const validNewPage = Math.max(1, newPage);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("page", String(validNewPage));
    setSearchParams(nextParams, {
      replace: nextParams.toString() === searchParams.toString(),
    });
  };

  const handleFilter = () => {
    const params: Record<string, string> = {
      page: "1",
    };

    if (codeFilter) params.code = codeFilter;
    if (titleFilter) params.title = titleFilter;
    if (reportItemNumberFrom)
      params.reportItemNumberFrom = reportItemNumberFrom;
    if (reportItemNumberTo) params.reportItemNumberTo = reportItemNumberTo;
    if (!impactError) params.impactError = "false";
    if (!impactWarning) params.impactWarning = "false";
    if (!scopeReport) params.scopeReport = "false";
    if (!scopeItem) params.scopeItem = "false";

    const nextSearchParams = new URLSearchParams(params);
    setSearchParams(params, {
      replace: nextSearchParams.toString() === searchParams.toString(),
    });
  };

  const handleClearFilter = () => {
    setCodeFilter("");
    setTitleFilter("");
    setReportItemNumberFrom("");
    setReportItemNumberTo("");
    setImpactError(true);
    setImpactWarning(true);
    setScopeReport(true);
    setScopeItem(true);
    setIsFiltersOpen(true);
    const nextParams = new URLSearchParams({ page: "1" });
    setSearchParams(
      { page: "1" },
      { replace: nextParams.toString() === searchParams.toString() }
    );
  };

  if (!representedEntity) {
    return <Loader />;
  }

  if (loadingHeader || loadingErrors) {
    return <Loader />;
  }

  if (isHeaderError) {
    return <ErrorDisplay message={handleApiError(headerError, t)} />;
  }

  if (isErrorsError) {
    return <ErrorDisplay message={handleApiError(errorsError, t)} />;
  }

  if (!headerData) {
    return <NoData />;
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <PageTitle>{t("errorsPage.errorsList")}</PageTitle>
      </div>

      <div className="space-y-1 text-sm text-black mb-6">
        <div>
          <span className="font-bold">
            {t("reportPage.reportInfo.vatPayerTitle")}:
          </span>{" "}
          {headerData.vatPayerInfo?.vatPayerTitle}
        </div>
        <div>
          <span className="font-bold">
            {t("reportPage.reportInfo.vatPayerCode")}:
          </span>{" "}
          {headerData.vatPayerInfo?.vatPayerIdentifierVAT}
        </div>
      </div>

      <hr className="my-6 border-gray-300" />

      <div className="mt-4">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <HeadlessTable table={versionTable} />
        </div>
      </div>

      <Accordion
        title={t("errorsPage.items")}
        isOpen={isFiltersOpen}
        onToggle={setIsFiltersOpen}
      >
        <div className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <FormInput
              label={t("errorsPage.table.code")}
              value={codeFilter}
              onChange={setCodeFilter}
            />
            <FormInput
              label={t("errorsPage.table.title")}
              value={titleFilter}
              onChange={setTitleFilter}
            />
            <FormInput
              label={t("reportItemsPage.filters.itemNumberFrom")}
              value={reportItemNumberFrom}
              onChange={val => {
                if (!val || Number(val) >= 0) setReportItemNumberFrom(val);
              }}
              type="number"
              min="0"
            />
            <FormInput
              label={t("reportItemsPage.filters.itemNumberTo")}
              value={reportItemNumberTo}
              onChange={val => {
                if (!val || Number(val) >= 0) setReportItemNumberTo(val);
              }}
              type="number"
              min="0"
            />
          </div>

          <div className="border-b border-gray-200 mb-6" />

          <div className="flex flex-col md:flex-row md:space-x-20 space-y-4 md:space-y-0 mb-6 px-10">
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                {t("errorsPage.table.validationRuleImpact")}
              </label>
              <div className="flex flex-col space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    checked={scopeReport}
                    onChange={e => setScopeReport(e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">
                    {t("validationRuleScope.REPORT")}
                  </span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    checked={scopeItem}
                    onChange={e => setScopeItem(e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">
                    {t("validationRuleScope.ITEM")}
                  </span>
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                {t("errorsPage.table.validationRuleScope")}
              </label>
              <div className="flex flex-col space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    checked={impactError}
                    onChange={e => setImpactError(e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">
                    {t("validationRuleImpact.ERROR")}
                  </span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    checked={impactWarning}
                    onChange={e => setImpactWarning(e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">
                    {t("validationRuleImpact.WARNING")}
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="border-b border-gray-200 mb-6" />

          <div className="flex justify-start space-x-3">
            <Button
              variant="primary"
              size="md"
              onClick={handleFilter}
              className="mb-0.5"
            >
              {t("reportItemsPage.filters.filter")}
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={handleClearFilter}
              className="mb-0.5"
            >
              {t("common.clearFilter")}
            </Button>
          </div>
        </div>
      </Accordion>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {t("errorsPage.errorsList")}
          </h2>
        </div>
        <HeadlessTable
          table={errorsTable}
          pagination={pagination}
          onPageChange={handlePageChange}
        />
        {(!errorsData || errorsData.length === 0) && (
          <div className="p-8 text-center text-gray-500">
            {t("errorsPage.noErrors")}
          </div>
        )}
      </div>
    </div>
  );
}
